/**
 * useUnreadMessages Hook (v2 - 使用 UnreadStatusManager)
 * 
 * 向後兼容的 Hook，內部使用新的 UnreadStatusManager
 * 
 * @author AI Assistant
 * @date 2025-12-08
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import UnreadStatusManager from '../services/UnreadStatusManager';

/**
 * 未讀消息管理 Hook
 * @returns {Object} Hook 返回值
 */
export function useUnreadMessages() {
  const userId = useMemo(() => localStorage.getItem('userId'), []);
  const [hasUnreadMap, setHasUnreadMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // 創建或獲取 manager 實例
  const manager = useMemo(() => {
    if (!userId) {
      const error = new Error('No userId found');
      console.warn('[useUnreadMessages]', error.message);
      setError(error);
      return null;
    }
    
    try {
      return new UnreadStatusManager(userId);
    } catch (err) {
      console.error('[useUnreadMessages] Failed to create manager:', err);
      setError(err);
      return null;
    }
  }, [userId]);
  
  // 訂閱狀態變化
  useEffect(() => {
    if (!manager) return;
    
    const unsubscribe = manager.subscribe((groupId, hasUnread) => {
      setHasUnreadMap(prev => ({
        ...prev,
        [groupId]: hasUnread
      }));
    });
    
    return unsubscribe;
  }, [manager]);
  
  /**
   * 獲取多個群組的未讀狀態
   * @param {number[]} groupIds - 群組 ID 列表
   */
  const fetchUnreadStatus = useCallback(async (groupIds = []) => {
    if (!manager || !groupIds || groupIds.length === 0) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const results = await manager.getStatusBatch(groupIds);
      setHasUnreadMap(prev => ({
        ...prev,
        ...results
      }));
    } catch (err) {
      console.error('[useUnreadMessages] Failed to fetch status:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [manager]);
  
  /**
   * 更新後端 lastReadTime
   * @param {number} groupId - 群組 ID
   * @param {number} [activityId] - 活動 ID（可選）
   */
  const updateLastReadTime = useCallback(async (groupId, activityId = null) => {
    if (!manager || !groupId) return;
    
    try {
      // updateBackend 內部會處理樂觀更新和防抖
      await manager.updateBackend(groupId, activityId);
    } catch (err) {
      console.error('[useUnreadMessages] Failed to update lastReadTime:', err);
      setError(err);
    }
  }, [manager]);
  
  /**
   * 本地標記為已讀（不更新後端）
   * @param {number} groupId - 群組 ID
   */
  const markAsReadLocal = useCallback((groupId) => {
    if (!manager || !groupId) return;
    
    manager.markAsRead(groupId);
  }, [manager]);
  
  /**
   * 清除指定群組的 cache
   * @param {number} [groupId] - 群組 ID（可選，不提供則清除所有）
   */
  const clearCache = useCallback((groupId) => {
    if (!manager) return;
    
    if (groupId) {
      manager.clearStatus(groupId);
      setHasUnreadMap(prev => {
        const next = { ...prev };
        delete next[groupId];
        return next;
      });
    } else {
      manager.clearAll();
      setHasUnreadMap({});
    }
  }, [manager]);
  
  return {
    hasUnreadMap,
    loading,
    error,
    fetchUnreadStatus,
    updateLastReadTime,
    markAsReadLocal,
    clearCache
  };
}

export default useUnreadMessages;
